// src/components/DocumentUpload.tsx

import { useState, useCallback } from 'react';
import JSZip from 'jszip';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Upload,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  Clock,
  FileText,
  ArrowUpDown,
  Tag as TagIcon,
  Plus,
  Trash2
} from 'lucide-react';
import { useUpload } from '../context/UploadContext';
import { useToast } from "@/components/ui/use-toast";

interface UploadFile {
  id: string;
  file: File;
  filename: filename;
  relativePath?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  tags: string[];
}

const DocumentUpload = () => {
  const { startUpload } = useUpload();
  const { toast } = useToast();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [newTag, setNewTag] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processZipFile = async (zipFile: File): Promise<File[]> => {
    try {
      const zip = new JSZip();
      const contents = await zip.loadAsync(zipFile);
      const extractedFiles: File[] = [];

      for (const [relativePath, zipEntry] of Object.entries(contents.files)) {
        if (!zipEntry.dir && !relativePath.startsWith('__MACOSX/') && !relativePath.includes('.DS_Store')) {
          const blob = await zipEntry.async('blob');
          const extension = zipEntry.name.split('.').pop()?.toLowerCase();
          const mimeType = {
            'pdf': 'application/pdf',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png'
          }[extension || ''] || 'application/octet-stream';

          const file = new File([blob], zipEntry.name, { type: mimeType });
          Object.defineProperty(file, 'relativePath', { value: relativePath });
          extractedFiles.push(file);
        }
      }

      return extractedFiles;
    } catch (error) {
      console.error('Error processing ZIP file:', error);
      throw new Error('Failed to process ZIP file');
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      setError(null);
      const newFiles: UploadFile[] = [];

      for (const file of acceptedFiles) {
        if (file.type === 'application/zip') {
          const extractedFiles = await processZipFile(file);
          newFiles.push(...extractedFiles.map(f => ({
            id: `${Math.random().toString(36).substr(2, 9)}-${f.name}`,
            file: f,
            relativePath: (f as any).relativePath,
            status: 'pending',
            progress: 0,
            tags: []
          })));
        } else {
          newFiles.push({
            id: `${Math.random().toString(36).substr(2, 9)}-${file.name}`,
            file,
            status: 'pending',
            progress: 0,
            tags: []
          });
        }
      }

      setFiles(prev => [...prev, ...newFiles]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error processing files');
      toast({
        variant: "destructive",
        title: "Error",
        description: err instanceof Error ? err.message : 'Error processing files',
      });
    }
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/zip': ['.zip']
    }
  });

  const handleUpload = async () => {
    if (!files.length) return;

    try {
      setError(null);
      setIsUploading(true);

      const pendingFiles = files.filter(f => f.status === 'pending');

      // Ensure each file has a name property
      const filesToUpload = pendingFiles.map(f => {
        const file = f.file;
        // If the file doesn't have a name, create a new File object with the name
        if (!file.name && f.filename) {
          return new File([file], f.filename, { type: file.type });
        }
        return file;
      });

      const allTags = Array.from(new Set(files.flatMap(f => f.tags)));

      await startUpload(filesToUpload, allTags);

      toast({
        title: "Upload Started",
        description: `Started uploading ${filesToUpload.length} files`,
      });

      // Clear only uploaded files after a short delay
      setTimeout(() => {
        setFiles(prev => prev.filter(f => !pendingFiles.includes(f)));
        setIsUploading(false);
      }, 1000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setIsUploading(false);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: err instanceof Error ? err.message : 'Upload failed',
      });
    }
  };

  const handleAddTag = () => {
    const tag = newTag.trim();
    if (!tag) return;

    setFiles(prev => prev.map(file => ({
      ...file,
      tags: [...new Set([...file.tags, tag])]
    })));

    setNewTag('');
  };

  const handleRemoveTag = (fileId: string, tagToRemove: string) => {
    setFiles(prev => prev.map(file => {
      if (file.id === fileId) {
        return {
          ...file,
          tags: file.tags.filter(tag => tag !== tagToRemove)
        };
      }
      return file;
    }));
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const formatFileSize = (bytes: number): string => {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Card>
        <CardHeader>
          <CardTitle>Upload Documents</CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
              ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
          >
            <input {...getInputProps()} />
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center">
                  <Upload className="h-6 w-6 text-blue-500" />
                </div>
              </div>
              <div>
                <p className="font-medium">Drop files here or click to browse</p>
                <p className="text-sm text-gray-500">
                  Supported formats: PDF, PNG, JPG, DOC, DOCX, ZIP
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              {error}
            </div>
          )}

          {files.length > 0 && (
            <>
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Add tags to all files..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddTag();
                      }
                    }}
                    className="pl-10"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddTag}
                  disabled={!newTag.trim()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tag
                </Button>
              </div>

              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {files.map(fileObj => (
                    <div key={fileObj.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <FileText className="h-6 w-6 text-blue-500" />
                          <div>
                            <p className="font-medium">{fileObj.file.name}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <span>{formatFileSize(fileObj.file.size)}</span>
                              <span>•</span>
                              <Clock className="h-3 w-3" />
                              <span>{new Date().toLocaleTimeString()}</span>
                            </div>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFile(fileObj.id)}
                          disabled={isUploading}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>

                      <div className="mt-4">
                        <div className="flex flex-wrap gap-2">
                          {fileObj.tags.map(tag => (
                            <Badge key={tag} variant="secondary" className="gap-1">
                              <TagIcon className="h-3 w-3" />
                              {tag}
                              <button
                                onClick={() => handleRemoveTag(fileObj.id, tag)}
                                className="ml-1 hover:text-red-500"
                                disabled={isUploading}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </CardContent>

        {files.length > 0 && (
          <CardFooter>
            <Button
              onClick={handleUpload}
              className="w-full"
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Preparing Upload...
                </>
              ) : (
                <>
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  Upload {files.length} {files.length === 1 ? 'file' : 'files'}
                </>
              )}
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default DocumentUpload;